import { matchPath } from "react-router";
import { toast } from "sonner";
import { ARTICLE_PREVIEW_PATH } from "@/features/blog/routes";
import { MY_CARD_PATH } from "@/features/card/routes";
import { getCustomFormPath } from "@/features/custom-forms/routes";
import { USER_PROFILE_PATH } from "@/features/userProfile/routes";
import { MandatoryCustomFormToast } from "@/shared/components/MandatoryCustomFormToast";
import type { MandatoryCustomForm } from "@/shared/types/askedAttributes.types";

// Chemins qu'un formulaire obligatoire en attente laisse ouverts : l'aide, la
// connexion, et la carte et les bons pour qu'un salarié puisse toujours payer en
// caisse. Pas le profil : il n'a rien à y compléter pour le formulaire.
export const MANDATORY_FORM_EXEMPTED_PATHS = [
  "/aide",
  "/bons",
  MY_CARD_PATH,
  "/connexion",
  "/deconnexion",
  "/mot-de-passe",
  "/reconfig",
];

// Un profil incomplet laisse en plus ouvert le profil, où le salarié le complète.
// Partagé entre le guard de route et l'interception des clics de la sidebar : les
// deux doivent rester alignés.
export const MANDATORY_GUARD_EXEMPTED_PATHS = [USER_PROFILE_PATH, ...MANDATORY_FORM_EXEMPTED_PATHS];

const startsWithAny = (pathname: string, paths: readonly string[]): boolean =>
  paths.some((path) => pathname.startsWith(path));

// A blog manager opens the article preview in a tab from the console: a guard sending them
// to their profile or a form would lose the preview. The path holds an id, hence matchPath
// rather than a prefix.
const isArticlePreviewPath = (pathname: string): boolean =>
  matchPath(ARTICLE_PREVIEW_PATH, pathname) !== null;

export const isMandatoryGuardExemptedPath = (pathname: string): boolean =>
  startsWithAny(pathname, MANDATORY_GUARD_EXEMPTED_PATHS) || isArticlePreviewPath(pathname);

// Chemin du premier formulaire obligatoire en attente, sauf si on y est déjà
// (pas de boucle de redirection) ou que le chemin est exempté.
export const getPendingFormRedirect = (pathname: string, form: MandatoryCustomForm | null): string | null => {
  if (!form || startsWithAny(pathname, MANDATORY_FORM_EXEMPTED_PATHS) || isArticlePreviewPath(pathname)) return null;
  const formPath = getCustomFormPath(form.slug);
  const isOnFormPage = pathname === formPath || pathname === `${formPath}/`;
  return isOnFormPage ? null : formPath;
};

// Id fixe : sonner remplace le toast au lieu d'en empiler un second quand la
// sidebar et le guard le déclenchent coup sur coup.
export const MANDATORY_CUSTOM_FORM_TOAST_ID = "mandatory-custom-form-toast";

type ShowMandatoryCustomFormToastOptions = {
  form: MandatoryCustomForm
  onNavigateToForm: () => void
  onDismiss?: () => void
};

export const showMandatoryCustomFormToast = ({ form, onNavigateToForm, onDismiss }: ShowMandatoryCustomFormToastOptions) => {
  toast.custom(
    (t) => (
      <MandatoryCustomFormToast
        toastId={t}
        form={form}
        onNavigateToForm={onNavigateToForm}
        onDismiss={onDismiss}
      />
    ),
    {
      id: MANDATORY_CUSTOM_FORM_TOAST_ID,
      duration: 8000,
    },
  );
};
